import { defineStore } from 'pinia'
import { type Ref, computed, onScopeDispose, ref, watch } from 'vue'

import { i18n } from '@/i18n'
import { type CallableError, parseCallableError } from '@/lib/firebase/errors'
import type { Subscription } from '@/lib/firebase/subscribe'
import { createRetainer } from '@/lib/live/retainer'
import {
  VisualStyleAssetUploadError,
  isEditorRoleRequiredReason,
  saveBrandPublishing as repoSaveBrandPublishing,
  setBrandMarket as repoSetBrandMarket,
  subscribeBrand as repoSubscribeBrand,
  subscribeBeliefMap as repoSubscribeBeliefMap,
  subscribeContentStrategy as repoSubscribeContentStrategy,
  subscribeStyleGuide as repoSubscribeStyleGuide,
  subscribeVisualStyle as repoSubscribeVisualStyle,
  updateBrandDocument as repoUpdateBrandDocument,
  uploadVisualStyleAsset as repoUploadVisualStyleAsset,
} from '@/repositories/brand'
import { subscribeCompetitors as repoSubscribeCompetitors } from '@/repositories/competition'
import {
  subscribeKnowledgePages as repoSubscribeKnowledgePages,
  subscribeLatestWebsiteRead as repoSubscribeLatestWebsiteRead,
} from '@/repositories/knowledge'
import {
  type StartBrandFactsInput,
  isStartBrandFactsReason,
  startBrandFacts as repoStartBrandFacts,
  subscribeBrandFacts as repoSubscribeBrandFacts,
} from '@/repositories/brand-facts'
import { subscribeCustomerProfile as repoSubscribeCustomerProfile } from '@/repositories/customer-profile'
import { useAuthStore } from '@/stores/auth'
import { useToastStore } from '@/stores/toast'
import type { PublishingTimes } from '@/lib/brand/publishing-times'
import type {
  Brand,
  BrandMarket,
  SetBrandMarketInput,
  BeliefMap,
  BeliefMapUpdate,
  ContentStrategy,
  PostingDay,
  SaveBrandPublishingInput,
  StyleGuide,
  UpdateBrandDocumentPayload,
  VisualStyle,
  VisualStyleAsset,
  VisualStyleUpdate,
} from '@/types/brand'
import { type BrandFacts, type BrandFactsUpdate, isBrandFactsApproved } from '@/types/brand-facts'
import type { Competitor } from '@/types/competition'
import type { CustomerProfile, CustomerProfileUpdate } from '@/types/customer-profile'
import type { KnowledgePage, WebsiteReadRun } from '@/types/knowledge'
import { hasBeliefMapContent, hasStyleGuideContent, styleGuideUpdates } from '@/types/brand'
import { failed, isError, isLoaded, isSettled, loading, type LoadState } from '@/types/load-state'

interface BrandScope {
  workspaceId: string
  brandId: string
}

interface ArtifactSlot<T> {
  sub: Subscription<T> | null
  stopWatch: (() => void) | null
}

function emptySlot<T>(): ArtifactSlot<T> {
  return { sub: null, stopWatch: null }
}

export type StartBrandFactsOutcome =
  | { readonly status: 'started'; readonly runId: string }
  | { readonly status: 'refused'; readonly reason: string | undefined }

interface ArtifactRead {
  readonly failed: () => boolean
  readonly close: () => void
}

export const useBrandStore = defineStore('brand', () => {
  const brand = ref<LoadState<Brand>>(loading()) as Ref<LoadState<Brand>>
  const beliefMap = ref<LoadState<BeliefMap>>(loading()) as Ref<LoadState<BeliefMap>>
  const contentStrategy = ref<LoadState<ContentStrategy>>(loading()) as Ref<
    LoadState<ContentStrategy>
  >
  const visualStyle = ref<LoadState<VisualStyle>>(loading()) as Ref<LoadState<VisualStyle>>
  const styleGuide = ref<LoadState<StyleGuide>>(loading()) as Ref<LoadState<StyleGuide>>
  const competitors = ref<LoadState<Competitor[]>>(loading()) as Ref<LoadState<Competitor[]>>
  const knowledgePages = ref<LoadState<KnowledgePage[]>>(loading()) as Ref<
    LoadState<KnowledgePage[]>
  >
  const websiteRead = ref<LoadState<WebsiteReadRun | null>>(loading()) as Ref<
    LoadState<WebsiteReadRun | null>
  >
  const brandFacts = ref<LoadState<BrandFacts>>(loading()) as Ref<LoadState<BrandFacts>>
  const customerProfile = ref<LoadState<CustomerProfile>>(loading()) as Ref<
    LoadState<CustomerProfile>
  >

  const brandSlot = emptySlot<Brand>()
  const beliefMapSlot = emptySlot<BeliefMap>()
  const contentStrategySlot = emptySlot<ContentStrategy>()
  const visualStyleSlot = emptySlot<VisualStyle>()
  const styleGuideSlot = emptySlot<StyleGuide>()
  const competitorsSlot = emptySlot<Competitor[]>()
  const knowledgePagesSlot = emptySlot<KnowledgePage[]>()
  const websiteReadSlot = emptySlot<WebsiteReadRun | null>()
  const brandFactsSlot = emptySlot<BrandFacts>()
  const customerProfileSlot = emptySlot<CustomerProfile>()

  function currentScope(): BrandScope | null {
    const auth = useAuthStore()
    const workspaceId = auth.claims?.workspaceId
    const brandId = auth.claims?.brandId
    if (!workspaceId || !brandId) return null
    return { workspaceId, brandId }
  }

  function openArtifact<T>(
    slot: ArtifactSlot<T>,
    state: Ref<LoadState<T>>,
    scope: BrandScope,
    subscribe: (scope: BrandScope) => Subscription<T>,
  ): void {
    if (slot.sub) return

    state.value = loading()
    const sub = subscribe(scope)
    slot.sub = sub
    slot.stopWatch = watch(
      sub.state,
      (next) => {
        state.value = next
      },
      { immediate: true },
    )
  }

  function closeArtifact<T>(slot: ArtifactSlot<T>, state: Ref<LoadState<T>>): void {
    slot.sub?.stop()
    slot.sub = null
    slot.stopWatch?.()
    slot.stopWatch = null
    state.value = loading()
  }

  function openAll(): void {
    const scope = currentScope()
    if (!scope) {
      const message = i18n.global.t('common.noWorkspaceSelected')
      brand.value = failed(message)
      beliefMap.value = failed(message)
      contentStrategy.value = failed(message)
      visualStyle.value = failed(message)
      styleGuide.value = failed(message)
      competitors.value = failed(message)
      knowledgePages.value = failed(message)
      websiteRead.value = failed(message)
      brandFacts.value = failed(message)
      customerProfile.value = failed(message)
      return
    }

    openArtifact(brandSlot, brand, scope, repoSubscribeBrand)
    openArtifact(beliefMapSlot, beliefMap, scope, repoSubscribeBeliefMap)
    openArtifact(contentStrategySlot, contentStrategy, scope, repoSubscribeContentStrategy)
    openArtifact(visualStyleSlot, visualStyle, scope, repoSubscribeVisualStyle)
    openArtifact(styleGuideSlot, styleGuide, scope, repoSubscribeStyleGuide)
    openArtifact(competitorsSlot, competitors, scope, repoSubscribeCompetitors)
    openArtifact(knowledgePagesSlot, knowledgePages, scope, repoSubscribeKnowledgePages)
    openArtifact(websiteReadSlot, websiteRead, scope, repoSubscribeLatestWebsiteRead)
    openArtifact(brandFactsSlot, brandFacts, scope, repoSubscribeBrandFacts)
    openArtifact(customerProfileSlot, customerProfile, scope, repoSubscribeCustomerProfile)
  }

  function artifactRead<T>(slot: ArtifactSlot<T>, state: Ref<LoadState<T>>): ArtifactRead {
    return { failed: () => isError(state.value), close: () => closeArtifact(slot, state) }
  }

  const reads: readonly ArtifactRead[] = [
    artifactRead(brandSlot, brand),
    artifactRead(beliefMapSlot, beliefMap),
    artifactRead(contentStrategySlot, contentStrategy),
    artifactRead(visualStyleSlot, visualStyle),
    artifactRead(styleGuideSlot, styleGuide),
    artifactRead(competitorsSlot, competitors),
    artifactRead(knowledgePagesSlot, knowledgePages),
    artifactRead(websiteReadSlot, websiteRead),
    artifactRead(brandFactsSlot, brandFacts),
    artifactRead(customerProfileSlot, customerProfile),
  ]

  function closeAll(): void {
    for (const read of reads) read.close()
  }

  const brandRetainer = createRetainer({ open: openAll, close: closeAll })
  onScopeDispose(brandRetainer.dispose)

  /** Re-reads every brand document whose feed failed; does nothing while no holder keeps the brand open. */
  function reopenFailed(): void {
    if (!brandRetainer.isOpen) return
    for (const read of reads) if (read.failed()) read.close()
    openAll()
  }

  const hasBrandFacts = computed(
    () => isLoaded(brandFacts.value) && isBrandFactsApproved(brandFacts.value.value),
  )

  const hasVisualStyle = computed(() => {
    if (!isLoaded(visualStyle.value)) return false
    const value = visualStyle.value.value
    return (
      value.colors.length > 0 ||
      value.fontFamily !== null ||
      value.logo !== null ||
      value.monogram !== null
    )
  })

  const hasBeliefMap = computed(
    () => isLoaded(beliefMap.value) && hasBeliefMapContent(beliefMap.value.value),
  )

  const hasStyleGuide = computed(
    () => isLoaded(styleGuide.value) && hasStyleGuideContent(styleGuide.value.value),
  )

  const brandName = computed(() => (isLoaded(brand.value) ? brand.value.value.name : undefined))

  const contentLanguage = computed(() =>
    isLoaded(brand.value) ? brand.value.value.contentLanguage : undefined,
  )

  const hasCompetitors = computed(
    () => isLoaded(competitors.value) && competitors.value.value.length > 0,
  )

  const hasProductKnowledge = computed(
    () => isLoaded(knowledgePages.value) && knowledgePages.value.value.length > 0,
  )

  /** Whether this brand is expected to end up with knowledge pages; a brand with no website expects none, so setup never waits on it. */
  const expectsProductKnowledge = computed(
    () => isLoaded(websiteRead.value) && websiteRead.value.value !== null,
  )

  const artifactPresence = computed(() => ({
    hasBrandFacts: hasBrandFacts.value,
    hasVisualStyle: hasVisualStyle.value,
    hasStyleGuide: hasStyleGuide.value,
    hasCompetitors: hasCompetitors.value,
    hasProductKnowledge: hasProductKnowledge.value,
    expectsProductKnowledge: expectsProductKnowledge.value,
  }))

  const artifactsLoaded = computed(() => {
    return (
      isSettled(visualStyle.value) &&
      isSettled(contentStrategy.value) &&
      isSettled(styleGuide.value) &&
      isSettled(competitors.value) &&
      isSettled(knowledgePages.value) &&
      isSettled(websiteRead.value)
    )
  })

  const isSaving = ref(false)
  const settingMarket = ref(false)

  function describeSaveFailure(failure: CallableError): string {
    return isEditorRoleRequiredReason(failure.reason)
      ? i18n.global.t('brand.toast.viewerRoleMessage')
      : failure.message
  }

  /** Sends a document's edits one after another and reports whether they all landed. */
  async function applyUpdates(
    payloads: readonly UpdateBrandDocumentPayload[],
    describeFailure: (failure: CallableError) => string = describeSaveFailure,
  ): Promise<boolean> {
    if (payloads.length === 0) return true

    isSaving.value = true
    try {
      for (const payload of payloads) {
        await repoUpdateBrandDocument(payload)
      }
      return true
    } catch (cause) {
      useToastStore().error(
        i18n.global.t('brand.toast.saveFailed'),
        describeFailure(parseCallableError(cause)),
      )
      return false
    } finally {
      isSaving.value = false
    }
  }

  /** Sets the brand's search market; resolves the market the server stored, or `null` when it refused. */
  async function setMarket(input: SetBrandMarketInput): Promise<BrandMarket | null> {
    settingMarket.value = true
    try {
      return (await repoSetBrandMarket(input)) ?? null
    } catch (cause) {
      useToastStore().error(
        i18n.global.t('brand.toast.saveFailed'),
        describeSaveFailure(parseCallableError(cause)),
      )
      return null
    } finally {
      settingMarket.value = false
    }
  }

  /** Saves the article author and trial page; resolves whether the server took it (a refusal is toasted). */
  async function saveBrandPublishing(input: SaveBrandPublishingInput): Promise<boolean> {
    isSaving.value = true
    try {
      await repoSaveBrandPublishing(input)
      return true
    } catch (cause) {
      useToastStore().error(
        i18n.global.t('brand.toast.saveFailed'),
        describeSaveFailure(parseCallableError(cause)),
      )
      return false
    } finally {
      isSaving.value = false
    }
  }

  async function saveBrandFacts(update: BrandFactsUpdate): Promise<boolean> {
    return applyUpdates([{ slug: 'brand-facts', data: update }])
  }

  /** Writes one customer-profile edit: a card's lines, or the identity and summary. */
  async function saveCustomerProfile(
    update: CustomerProfileUpdate,
    describeFailure?: (failure: CallableError) => string,
  ): Promise<boolean> {
    return applyUpdates([{ slug: 'customer-profile', data: update }], describeFailure)
  }

  const startingBrandFactsRead = ref(false)

  /** Opens a brand-facts read of two links; a refusal the form can show under a field is returned without a toast. */
  async function startBrandFactsRead(input: StartBrandFactsInput): Promise<StartBrandFactsOutcome> {
    startingBrandFactsRead.value = true
    try {
      const { runId } = await repoStartBrandFacts(input)
      return { status: 'started', runId }
    } catch (cause) {
      const parsed = parseCallableError(cause)
      if (!isStartBrandFactsReason(parsed.reason)) {
        useToastStore().error(i18n.global.t('setup.errors.startFailed'), parsed.message)
      }
      return { status: 'refused', reason: parsed.reason }
    } finally {
      startingBrandFactsRead.value = false
    }
  }

  async function saveVisualStyle(update: VisualStyleUpdate): Promise<boolean> {
    return applyUpdates([{ slug: 'visual-style', data: update }])
  }

  /** Uploads a brand mark image and resolves the asset to stage into a draft, or `null` on a rejected file or failed upload (toasted). */
  async function uploadVisualStyleMark(file: File): Promise<VisualStyleAsset | null> {
    const scope = currentScope()
    if (!scope) {
      useToastStore().error(i18n.global.t('common.noWorkspaceSelected'))
      return null
    }

    try {
      const { src } = await repoUploadVisualStyleAsset(scope, file)
      return { name: file.name, src }
    } catch (cause) {
      const message =
        cause instanceof VisualStyleAssetUploadError
          ? cause.message
          : i18n.global.t('brand.visualStyle.errors.uploadFailed')
      useToastStore().error(i18n.global.t('brand.toast.markUploadFailed'), message)
      return null
    }
  }

  async function saveBeliefMapEdit(
    data: BeliefMapUpdate,
    describeFailure?: (failure: CallableError) => string,
  ): Promise<boolean> {
    return applyUpdates([{ slug: 'belief-map' as const, data }], describeFailure)
  }

  /** Saves the posting cadence; `postsPerWeek` is derived from the day count. */
  async function saveCadence(
    preferredDays: readonly PostingDay[],
    preferredTimes: PublishingTimes,
  ): Promise<boolean> {
    return applyUpdates([
      {
        slug: 'content-strategy',
        data: {
          section: 'cadence',
          cadence: { postsPerWeek: preferredDays.length, preferredDays, preferredTimes },
        },
      },
    ])
  }

  async function saveStyleGuide(next: StyleGuide): Promise<boolean> {
    if (!isLoaded(styleGuide.value)) return false
    return applyUpdates(
      styleGuideUpdates(styleGuide.value.value, next).map((data) => ({
        slug: 'style-guide' as const,
        data,
      })),
    )
  }

  return {
    brand,
    beliefMap,
    contentStrategy,
    visualStyle,
    styleGuide,
    competitors,
    knowledgePages,
    websiteRead,
    brandFacts,
    customerProfile,
    brandName,
    contentLanguage,
    artifactPresence,
    artifactsLoaded,
    isSaving,
    settingMarket,
    startingBrandFactsRead,
    retain: brandRetainer.retain,
    reopenFailed,
    setMarket,
    saveBrandPublishing,
    saveBrandFacts,
    saveCustomerProfile,
    startBrandFactsRead,
    saveVisualStyle,
    uploadVisualStyleMark,
    saveBeliefMapEdit,
    hasBeliefMap,
    saveCadence,
    saveStyleGuide,
  }
})
